import { Stack } from '@mui/material'
import Box from '@mui/material/Box'
import { get } from 'lodash'
import { ReactElement, useEffect, useState } from 'react'
import { Form } from 'react-final-form'
import { Feature_Value, Project_Goal, Project_Measure, Query_Root } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import {
  queryFeaturesByProject,
  queryMeasuresByProject,
} from 'src/screens/shared/project/details/goals-and-measures/goal/goalQueries'
import { TEXT_LENGTH } from 'src/shared/constants/constants'
import { PROJECT_GOAL_RELATED_FEATURE_CODES } from 'src/shared/constants/feature-constants'
import { Option } from 'src/shared/form/control'
import { HtmlEditorField } from 'src/shared/form/control/HtmlEditorField'
import { MultiSelectField } from 'src/shared/form/control/MultiSelectField'
import { TextField } from 'src/shared/form/control/TextField'
import { DirtyFormSpy } from 'src/shared/form/dirty/DirtyFormSpy'
import { createDecorators } from 'src/shared/form/utils/decorators'
import { composeValidators, maxChar, required } from 'src/shared/form/validation/validators'
import { HtmlSanitizer } from 'src/shared/utils/HtmlSanitizer'
import { useUserLocale } from 'src/user/UserContext'
import { useClient } from 'urql'

export interface GoalFormValues {
  projectBaseId: number
  goalId?: number
  name: string
  description: string
  sortNumber: number
  version?: number
  measureIds?: number[]
  projectAreaIds?: number[]
}

interface ProjectGoalProps {
  initialValues: GoalFormValues
  showGoalFeatures: boolean
  onSave: (values: GoalFormValues) => Promise<void>
}

export const initialValuesFactory = (projectBaseId: number, goal?: Project_Goal): GoalFormValues => {
  const selectedMeasureIds =
    goal?.project_goal_measures.map((project_goal_measure) => project_goal_measure.project_measure.id) ?? []
  const selectedProjectAreas = goal?.project_goal_features?.map((features) => features.feature_value_id) ?? []

  return {
    projectBaseId: projectBaseId,
    goalId: goal?.id || undefined,
    name: goal?.name || '',
    description: HtmlSanitizer.sanitize(goal?.description),
    sortNumber: goal?.sort_number || 1,
    version: goal?.version || 0,
    measureIds: selectedMeasureIds,
    projectAreaIds: selectedProjectAreas,
  }
}

const decorators = createDecorators()

export const GoalForm = ({ initialValues, onSave, showGoalFeatures }: ProjectGoalProps): ReactElement => {
  const { getMessage } = useMessageSource()
  const urqlClient = useClient()
  const language = useUserLocale()
  const projectBaseId = initialValues.projectBaseId
  const [measureOptions, setMeasureOptions] = useState<Option[] | undefined>()
  const [projectMainArea, setProjectMainArea] = useState<Option[]>([])
  const [projectSecondArea, setProjectSecondArea] = useState<Option[]>([])

  useEffect(() => {
    const initializeAreaFeatureOptions = async () => {
      const { data: mainAreaFeatures } = await urqlClient
        .query<{ project_base: Query_Root['project_base'] }>(queryFeaturesByProject, {
          projectBaseWhere: { pf_pgv_projects: { project_base_id: { _eq: projectBaseId } } },
          featureTypeCode: PROJECT_GOAL_RELATED_FEATURE_CODES.PRIORITY_AREA_INTERVENTION_MAIN,
        })
        .toPromise()

      const { data: secondAreaFeatures } = await urqlClient
        .query<{ project_base: Query_Root['project_base'] }>(queryFeaturesByProject, {
          projectBaseWhere: { pf_pgv_projects: { project_base_id: { _eq: projectBaseId } } },
          featureTypeCode: PROJECT_GOAL_RELATED_FEATURE_CODES.PRIORITY_AREA_INTERVENTION_CROSSSECTION,
        })
        .toPromise()

      if (mainAreaFeatures && mainAreaFeatures.project_base && secondAreaFeatures && secondAreaFeatures.project_base) {
        const mainAreaOptions = mapFeatureValuesToOptions(
          mainAreaFeatures.project_base?.[0].feature_base?.feature_values,
        )

        const secondAreaOptions = mapFeatureValuesToOptions(
          secondAreaFeatures.project_base?.[0].feature_base?.feature_values,
        )

        setProjectSecondArea(secondAreaOptions)
        setProjectMainArea(mainAreaOptions)
      }
    }

    const initializeMeasureOptions = async () => {
      const { data } = await urqlClient
        .query<{ project_base: Query_Root['project_base'] }, { projectBaseId: number }>(queryMeasuresByProject, {
          projectBaseId: projectBaseId,
        })
        .toPromise()

      if (data && data.project_base) {
        const options = mapProjectMeasuresToOptions(data?.project_base[0].project_measures)
        setMeasureOptions(options)
      }
    }

    initializeMeasureOptions()

    if (showGoalFeatures) {
      initializeAreaFeatureOptions()
    }
  }, [getMessage, urqlClient, projectBaseId, setMeasureOptions, setProjectMainArea, setProjectSecondArea])

  const mapProjectMeasuresToOptions = (projectMeasures: Array<Project_Measure> | undefined): Option[] => {
    return (
      projectMeasures?.map((project_measure) => ({
        label: `${getMessage('label.measure.id.character')}${project_measure.sort_number}: ${project_measure.name}`,
        value: project_measure.id,
        sortNumber: project_measure.sort_number || 0,
      })) ?? []
    )
  }

  const mapFeatureValuesToOptions = (featureValues: Array<Feature_Value> | undefined): Option[] => {
    return (
      featureValues?.map((feature_values) => ({
        label: get(feature_values.feature_config.names, language, ''),
        value: feature_values.id,
        sortNumber: feature_values.feature_config.sort_number || 0,
      })) ?? []
    )
  }

  return (
    <Box>
      {initialValues && measureOptions && (
        <Form<GoalFormValues>
          initialValues={initialValues}
          onSubmit={onSave}
          decorators={decorators}
          render={({ handleSubmit }) => {
            return (
              <form id="goal-form" onSubmit={handleSubmit}>
                <Stack spacing={2}>
                  <TextField
                    required
                    label={getMessage('label.title')}
                    type="text"
                    name="name"
                    validate={composeValidators(required(), maxChar(TEXT_LENGTH.S))}
                  />
                  <HtmlEditorField
                    required
                    label={getMessage('label.description')}
                    name="description"
                    validate={maxChar(TEXT_LENGTH.L)}
                  />
                  <MultiSelectField
                    options={measureOptions}
                    name="measureIds"
                    label={getMessage('label.related.measures')}
                    unavailableOptionsMessage={getMessage('label.related.measures.add.measures')}
                  />

                  {showGoalFeatures && (
                    <>
                      <MultiSelectField
                        options={projectMainArea}
                        required
                        name="projectAreaIds"
                        label={getMessage('label.related.features_area_main')}
                        unavailableOptionsMessage={getMessage('label.related.goals.add.features_area_main')}
                      />
                      <MultiSelectField
                        options={projectSecondArea}
                        required
                        name="projectAreaIds"
                        label={getMessage('label.related.features_area_secondary')}
                        unavailableOptionsMessage={getMessage('label.related.goals.add.features_area_secondary')}
                      />
                    </>
                  )}
                  <DirtyFormSpy />
                </Stack>
              </form>
            )
          }}
        />
      )}
    </Box>
  )
}
